import { add, getUnixTime } from 'date-fns';
import { snoozedReopenTime } from './snoozeHelpers';

// Every preset is a plain offset from now, keeping the same time of day. That
// is deliberate: an offset needs no timezone reasoning, so "tomorrow" means the
// same thing to an agent in Lahore and one in Berlin. Keys map to
// CONVERSATION.REMINDER.PRESETS.* in the locale files.
export const REMINDER_PRESETS = [
  'in_an_hour',
  'in_two_hours',
  'tomorrow',
  'in_two_days',
  'next_week',
  'custom',
];

export const CUSTOM_REMINDER_PRESET = 'custom';

const PRESET_OFFSETS = {
  in_an_hour: { hours: 1 },
  in_two_hours: { hours: 2 },
  tomorrow: { days: 1 },
  in_two_days: { days: 2 },
  next_week: { days: 7 },
};

/**
 * Resolves a preset key to the moment the reminder should fire.
 *
 * @param {string} preset One of REMINDER_PRESETS
 * @param {Date} now Reference time, injectable for tests
 * @returns {Date|null} null for 'custom' and unknown keys
 */
export const getReminderTime = (preset, now = new Date()) => {
  const offset = PRESET_OFFSETS[preset];
  return offset ? add(now, offset) : null;
};

/**
 * The API takes snoozed_until as unix seconds, same as the snooze flow.
 *
 * @param {string} preset One of REMINDER_PRESETS
 * @param {Date} now Reference time, injectable for tests
 * @returns {number|null}
 */
export const getReminderTimestamp = (preset, now = new Date()) => {
  const time = getReminderTime(preset, now);
  return time ? getUnixTime(time) : null;
};

/**
 * Reads the reminder off a conversation. Stored under custom_attributes so it
 * stays editable, unlike a message.
 *
 * @param {object} conversation
 * @returns {{note: string, due_at: string, set_by_name: string}|null}
 */
export const getConversationReminder = conversation => {
  const reminder = conversation?.custom_attributes?.reminder;
  if (!reminder || !reminder.note) return null;
  return reminder;
};

export const hasReminder = conversation =>
  Boolean(getConversationReminder(conversation));

/**
 * Formats the due time the same way the header formats a snooze, so the two
 * read consistently.
 *
 * @param {string|Date} dueAt ISO 8601 string or Date
 * @returns {string|null}
 */
export const formatReminderDueTime = dueAt => snoozedReopenTime(dueAt);

/**
 * Formats a unix timestamp for the "fires on ..." line, so an agent always sees
 * the exact moment rather than trusting a preset label.
 *
 * @param {number|null} timestamp unix seconds
 * @returns {string|null}
 */
export const formatReminderTimestamp = timestamp =>
  timestamp
    ? snoozedReopenTime(new Date(timestamp * 1000).toISOString())
    : null;

/**
 * Value for a datetime-local input, which wants local time with no zone
 * suffix. toISOString would shift it to UTC and show the wrong clock time.
 *
 * @param {Date} date
 * @returns {string} YYYY-MM-DDTHH:mm
 */
export const toDateTimeLocalValue = date => {
  const pad = value => String(value).padStart(2, '0');
  return [
    date.getFullYear(),
    '-',
    pad(date.getMonth() + 1),
    '-',
    pad(date.getDate()),
    'T',
    pad(date.getHours()),
    ':',
    pad(date.getMinutes()),
  ].join('');
};
